import {defineStore} from "pinia";
import {
    Broadcast,
    groupedMessagesData,
    TrailerData,
    Venue,
    VideoItem,
    Billing,
    Brand,
    Genre,
    Language,
    ContentItemLinks,
    ContentStream,
    PageData, fetchContentItem, fetchContentStream
} from "../services/content";
import {ref} from "vue";

export const useContentStore = defineStore("contentItem", () => {
    const id = ref(0)
    const itemId = ref("")
    const mainItemId = ref("")
    const reportChannelId = ref(0)
    const reportTitle = ref("")
    const description = ref("")
    const title = ref("")
    const rating_score = ref(0.0)
    const rating_count = ref(0)
    const page_url = ref("")
    const trailer = ref<TrailerData | null>(null)
    const tags = ref<string[]>([])
    const groupedMessages = ref<groupedMessagesData | null>(null)
    const languages = ref<Language[]>([])
    const broadcasts = ref<Broadcast[] | null>(null)
    const video_types = ref<string[]>([])
    const venue = ref<Venue | null>(null)
    const billing = ref<Billing | null>(null)
    const links = ref<ContentItemLinks | null>(null)
    const featuredImage = ref<any[] | null>(null)
    const contentStream = ref<ContentStream | null>(null)
    const brands = ref<Brand[]>([])
    const genres = ref<Genre[]>([])
    const chatRoom = ref<string | null>(null)

    function initialData(data: PageData) {
        id.value = data.id
        itemId.value = data.item_id
        mainItemId.value = data.main_item_id
        title.value = data.title
        broadcasts.value = data.broadcasts || null
        featuredImage.value = data.featuredImage || null

    }

    async function fetchItem(contentId: string){
        try{
            const data: VideoItem = await fetchContentItem(contentId);
            id.value = data.id
            itemId.value = contentId
            reportChannelId.value = data.report_channel_id
            reportTitle.value = data.report_title
            title.value = data.title
            description.value = data.description
            rating_score.value = data.rating_score
            rating_count.value = data.rating_count
            page_url.value = data.page_url
            trailer.value = data.trailer || null
            tags.value = data.tags ? data.tags : []
            groupedMessages.value = data.groupedMessages || null
            languages.value = data.languages || []
            broadcasts.value = data.broadcasts || null
            video_types.value = data.video_types
            venue.value = data.venue || null
            billing.value = data.billing || null
            links.value = data.links || null
            brands.value = data.brands
            genres.value = data.genres
            chatRoom.value = data.chat_room || null
            // console.log("loaded item", itemId.value, data)
            reCalculateTags()
            window.postMessage({e: "watch-later", state: tags.value.includes("bookmarked")})
            return true
        }catch (error){
            console.log(error)
            return false
        }
    }

    async function fetchStream(deviceType: string){
        return fetchContentStream(itemId.value, deviceType).then(data => {
            // console.log("loaded streams for", itemId)
            contentStream.value = data
        })
    }

    function reCalculateTags(){
        const airTime = broadcasts.value?.filter(row => {
            return row.is_live === true
        }).pop()

        if (!airTime)
            return

        const currentTime = new Date()
        if (new Date(airTime.air_start_time) > currentTime){
            if (!tags.value.includes("upcoming")){
                tags.value.push("upcoming")
            }
        }else if(new Date(airTime.air_start_time) < currentTime && new Date(airTime.air_end_time) > currentTime){
            if (tags.value.includes("upcoming")){
                tags.value = tags.value.filter((row) => {
                    return row !== "upcoming"
                })
            }
            tags.value.push("on_air")
        }
    }

    function addToCartEvent(){
        if (!window.dataLayer || !billing.value?.price)
            return
        window.dataLayer.push({
            event: "add_to_cart",
            ecomm_prodid: itemId.value,
            ecomm_pagetype: "cart",
            ecomm_totalvalue: billing.value.price?.amount,
            site: "fite"
        })
    }

    function addTag(tag: string){
        if (!tags.value.includes(tag)){
            tags.value.push(tag)
        }
    }

    function removeTag(tag: string){
        if (tags.value.includes(tag)){
            tags.value = tags.value.filter((row) => {
                return row !== tag
            })
        }
    }

    return {
        id, itemId, mainItemId, reportChannelId, reportTitle, title, rating_score, rating_count, page_url, trailer, tags,
        groupedMessages, languages, broadcasts, video_types, venue, billing, brands, genres, links, chatRoom,
        contentStream, featuredImage, description,
        initialData,
        fetchItem,
        fetchStream,
        reCalculateTags,
        addToCartEvent,
        addTag,
        removeTag
    }
})
